import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AvatarMetadataState,
  AvatarMotionIntentState,
  AvatarMotionState,
  AvatarState,
  ClientMessage,
  RoomStatePayload,
  RoomLayoutShape,
  ServerMessage,
} from "../../shared/types";
import { getDoorExitCell } from "../../shared/room-layout";
import { wsBase, type Session } from "../lib/api";

export interface ChatLogEntry {
  id: string;
  fromName: string;
  role: "player" | "npc";
  text: string;
  timestamp: number;
}

export interface RoomMotionFeedEntry extends AvatarMotionState {
  sampleTimeMs: number;
  updateSeq: number;
}

export interface RoomMotionFeed {
  byId: Map<string, RoomMotionFeedEntry>;
  interpolationDelayMs: number;
}

const DEFAULT_INTERPOLATION_DELAY_MS = 130;
const MIN_INTERPOLATION_DELAY_MS = 95;
const MAX_INTERPOLATION_DELAY_MS = 240;
const ARRIVAL_EMA_ALPHA = 0.12;
const JITTER_DELAY_MULTIPLIER = 2.2;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function useRoomConnection(
  session: Session | null,
  roomId: string | null,
  onKicked?: (message: string) => void,
  onDMNotification?: (
    fromAddress: string,
    fromName: string,
    text?: string
  ) => void,
  onFriendRequestNotification?: (
    fromAddress: string,
    fromName: string
  ) => void,
  onIdleWarning?: (secondsLeft: number) => void
) {
  const [roomState, setRoomState] = useState<RoomStatePayload | null>(null);
  const [stateSampleTimeMs, setStateSampleTimeMs] = useState<number | null>(
    null
  );
  const [selfId, setSelfId] = useState<string | null>(null);
  const [waitlistPosition, setWaitlistPosition] = useState<number | null>(null);
  const [chatLog, setChatLog] = useState<ChatLogEntry[]>([]);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);
  const chatSeqRef = useRef(0);
  const pendingReliableMessagesRef = useRef<ClientMessage[]>([]);
  const onDMNotificationRef = useRef(onDMNotification);
  onDMNotificationRef.current = onDMNotification;
  const onFriendRequestNotificationRef = useRef(onFriendRequestNotification);
  onFriendRequestNotificationRef.current = onFriendRequestNotification;
  const onIdleWarningRef = useRef(onIdleWarning);
  onIdleWarningRef.current = onIdleWarning;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const minServerOffsetMsRef = useRef<number | null>(null);
  const lastStateTimestampRef = useRef<number>(0);
  const lastStateSeqRef = useRef<number>(0);
  const lastMotionPacketArrivedAtMsRef = useRef<number | null>(null);
  const interArrivalEmaMsRef = useRef<number | null>(null);
  const jitterEmaMsRef = useRef<number>(0);
  const motionFeedRef = useRef<RoomMotionFeed>({
    byId: new Map<string, RoomMotionFeedEntry>(),
    interpolationDelayMs: DEFAULT_INTERPOLATION_DELAY_MS,
  });

  const enqueueReliableMessage = useCallback((message: ClientMessage) => {
    const queue = pendingReliableMessagesRef.current;
    queue.push(message);
    if (queue.length > 24) {
      queue.splice(0, queue.length - 24);
    }
  }, []);

  const sendSocket = useCallback((message: ClientMessage) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      if (message.type === "chat" || message.type === "ask_bot") {
        enqueueReliableMessage(message);
      }
      return;
    }
    try {
      socket.send(JSON.stringify(message));
    } catch {
      if (message.type === "chat" || message.type === "ask_bot") {
        enqueueReliableMessage(message);
      }
    }
  }, [enqueueReliableMessage]);

  useEffect(() => {
    if (!session?.address || !roomId) return;

    let cancelled = false;
    let kicked = false;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let retryDelay = 1000; // start at 1s, max 10s
    const MAX_RETRY_DELAY = 10_000;

    const connect = () => {
      if (cancelled) return;

      const ws = new WebSocket(
        `${wsBase()}/ws/${encodeURIComponent(
          roomId
        )}?token=${encodeURIComponent(session.token)}&name=${encodeURIComponent(
          session.displayName
        )}&color=${encodeURIComponent(session.avatarColor ?? "#000000")}`
      );
      socketRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        retryDelay = 1000; // reset on successful connect
        if (pendingReliableMessagesRef.current.length > 0) {
          const queued = pendingReliableMessagesRef.current.splice(0);
          for (let index = 0; index < queued.length; index += 1) {
            const queuedMessage = queued[index];
            if (ws.readyState !== WebSocket.OPEN) {
              pendingReliableMessagesRef.current.unshift(...queued.slice(index));
              break;
            }
            try {
              ws.send(JSON.stringify(queuedMessage));
            } catch {
              pendingReliableMessagesRef.current.unshift(
                ...queued.slice(index)
              );
              break;
            }
          }
          if (pendingReliableMessagesRef.current.length > 24) {
            pendingReliableMessagesRef.current.splice(
              0,
              pendingReliableMessagesRef.current.length - 24
            );
          }
        }
      };

      ws.onclose = () => {
        setConnected(false);
        if (socketRef.current === ws) socketRef.current = null;
        // Reconnect unless intentionally closed or kicked
        if (!cancelled && !kicked) {
          retryTimeout = setTimeout(() => {
            retryTimeout = null;
            connect();
          }, retryDelay);
          retryDelay = Math.min(retryDelay * 1.5, MAX_RETRY_DELAY);
        }
      };

      const mapServerTimestampToLocalMs = (serverTimestamp: number): number => {
        const localEpochNow = performance.timeOrigin + performance.now();
        const offset = localEpochNow - serverTimestamp;
        if (
          minServerOffsetMsRef.current == null ||
          offset < minServerOffsetMsRef.current
        ) {
          minServerOffsetMsRef.current = offset;
        }
        const mappedLocalEpoch =
          serverTimestamp + (minServerOffsetMsRef.current ?? offset);
        return mappedLocalEpoch - performance.timeOrigin;
      };
      const recordMotionPacketArrival = (): void => {
        const localNow = performance.now();
        const previousArrival = lastMotionPacketArrivedAtMsRef.current;
        lastMotionPacketArrivedAtMsRef.current = localNow;
        if (previousArrival == null) {
          return;
        }

        const interArrival = Math.max(1, localNow - previousArrival);
        // Ignore bursty back-to-back packets from non-correction channels.
        if (interArrival < 35) {
          return;
        }
        const previousInterArrivalEma =
          interArrivalEmaMsRef.current ?? interArrival;
        const nextInterArrivalEma =
          previousInterArrivalEma +
          (interArrival - previousInterArrivalEma) * ARRIVAL_EMA_ALPHA;
        interArrivalEmaMsRef.current = nextInterArrivalEma;

        const jitterSample = Math.abs(interArrival - nextInterArrivalEma);
        const nextJitterEma =
          jitterEmaMsRef.current +
          (jitterSample - jitterEmaMsRef.current) * ARRIVAL_EMA_ALPHA;
        jitterEmaMsRef.current = nextJitterEma;

        const nextDelay = clamp(
          nextInterArrivalEma + nextJitterEma * JITTER_DELAY_MULTIPLIER + 16,
          MIN_INTERPOLATION_DELAY_MS,
          MAX_INTERPOLATION_DELAY_MS
        );
        motionFeedRef.current.interpolationDelayMs = nextDelay;
      };
      const shouldApplyStateMessage = (
        seq: number | undefined,
        timestamp: number
      ): boolean => {
        if (Number.isFinite(seq)) {
          const previousSeq = lastStateSeqRef.current;
          if ((seq as number) <= previousSeq) {
            return false;
          }
          lastStateSeqRef.current = seq as number;
          if (Number.isFinite(timestamp)) {
            lastStateTimestampRef.current = timestamp;
          }
          return true;
        }
        if (!Number.isFinite(timestamp)) {
          return true;
        }
        const previousTimestamp = lastStateTimestampRef.current;
        if (timestamp < previousTimestamp) {
          return false;
        }
        lastStateTimestampRef.current = timestamp;
        return true;
      };
      const applyMotionUpdates = (
        updates: AvatarMotionState[],
        sampleTimeMs: number,
        updateSeq: number
      ): void => {
        const byId = motionFeedRef.current.byId;
        for (const update of updates) {
          byId.set(update.id, {
            ...update,
            sampleTimeMs,
            updateSeq,
          });
        }
      };
      const applyMotionIntentUpdates = (
        updates: AvatarMotionIntentState[],
        sampleTimeMs: number,
        updateSeq: number
      ): void => {
        const byId = motionFeedRef.current.byId;
        for (const update of updates) {
          byId.set(update.id, {
            id: update.id,
            x: update.x,
            y: update.y,
            z: update.z,
            targetX: update.targetX,
            targetY: update.targetY,
            targetZ: update.targetZ,
            facing: update.facing,
            sampleTimeMs,
            updateSeq,
          });
        }
      };
      const pruneMotionFeedToSnapshot = (state: {
        players: Array<{ id: string }>;
        bots: Array<{ id: string }>;
      }): void => {
        const keepIds = new Set([
          ...state.players.map((avatar) => avatar.id),
          ...state.bots.map((avatar) => avatar.id),
        ]);
        const byId = motionFeedRef.current.byId;
        for (const id of byId.keys()) {
          if (!keepIds.has(id)) {
            byId.delete(id);
          }
        }
      };
      const removeMotionFeedEntries = (ids: string[]): void => {
        if (ids.length === 0) {
          return;
        }
        const byId = motionFeedRef.current.byId;
        for (const id of ids) {
          byId.delete(id);
        }
      };
      const toAvatarStateFromMetadata = (
        metadata: AvatarMetadataState,
        current?: AvatarState,
        layout?: RoomLayoutShape | null
      ): AvatarState => {
        const motion = motionFeedRef.current.byId.get(metadata.id);
        // When a brand-new player appears via state_delta there is no motion
        // feed entry and no `current` avatar. Default to the door exit cell
        // so they appear at the doorway instead of (0,0).
        let defaultX = 0;
        let defaultZ = 0;
        if (!motion && !current && layout) {
          const [ex, ez] = getDoorExitCell(layout);
          defaultX = ex;
          defaultZ = ez;
        }
        // Positions in `current` can be stale because movement streams separately.
        // Prefer motion-feed snapshots when rebuilding from metadata patches.
        const fallbackX = motion?.x ?? current?.x ?? defaultX;
        // Sitting avatars should keep their settled seat height across metadata-only
        // updates (chat/wave) even when motion feed is stale.
        const fallbackY =
          metadata.isSitting === true
            ? current?.y ?? motion?.y ?? 0
            : motion?.y ?? current?.y ?? 0;
        const fallbackZ = motion?.z ?? current?.z ?? defaultZ;
        const fallbackTargetY =
          metadata.isSitting === true
            ? current?.targetY ?? motion?.targetY ?? fallbackY
            : motion?.targetY ?? current?.targetY ?? fallbackY;
        return {
          id: metadata.id,
          address: metadata.address,
          name: metadata.name,
          role: metadata.role,
          isBot: metadata.isBot,
          color: metadata.color,
          isDancing: metadata.isDancing,
          isSitting: metadata.isSitting,
          sittingAssetId: metadata.sittingAssetId,
          sittingSeatIndex: metadata.sittingSeatIndex,
          waveAt: metadata.waveAt,
          x: fallbackX,
          y: fallbackY,
          z: fallbackZ,
          targetX: motion?.targetX ?? current?.targetX ?? fallbackX,
          targetY: fallbackTargetY,
          targetZ: motion?.targetZ ?? current?.targetZ ?? fallbackZ,
          facing: motion?.facing ?? current?.facing ?? 0,
          path: current?.path,
          lastMessage: metadata.lastMessage,
          lastMessageAt: metadata.lastMessageAt,
        };
      };
      const hasMetadataChanged = (
        current: AvatarState | undefined,
        metadata: AvatarMetadataState
      ): boolean => {
        if (!current) {
          return true;
        }
        return (
          current.address !== metadata.address ||
          current.name !== metadata.name ||
          current.role !== metadata.role ||
          current.isBot !== metadata.isBot ||
          current.color !== metadata.color ||
          current.isDancing !== metadata.isDancing ||
          current.isSitting !== metadata.isSitting ||
          current.sittingAssetId !== metadata.sittingAssetId ||
          current.sittingSeatIndex !== metadata.sittingSeatIndex ||
          current.waveAt !== metadata.waveAt ||
          current.lastMessage !== metadata.lastMessage ||
          current.lastMessageAt !== metadata.lastMessageAt
        );
      };
      const mergeMetadataPatchIntoAvatars = (
        current: RoomStatePayload["players"],
        updates: AvatarMetadataState[],
        removedIds: string[],
        fullSync: boolean,
        layout?: RoomLayoutShape | null
      ): RoomStatePayload["players"] => {
        if (fullSync) {
          const currentById = new Map(
            current.map((avatar) => [avatar.id, avatar])
          );
          return updates.map((metadata) => {
            const existing = currentById.get(metadata.id);
            if (!hasMetadataChanged(existing, metadata) && existing) {
              return existing;
            }
            return toAvatarStateFromMetadata(metadata, existing, layout);
          });
        }
        const removed = new Set(removedIds);
        const next = current.filter((avatar) => !removed.has(avatar.id));
        const indexById = new Map(
          next.map((avatar, index) => [avatar.id, index])
        );
        for (const metadata of updates) {
          const index = indexById.get(metadata.id);
          const existing = index != null ? next[index] : undefined;
          if (!hasMetadataChanged(existing, metadata) && existing) {
            if (index == null) {
              indexById.set(existing.id, next.length);
              next.push(existing);
            }
            continue;
          }
          const merged = toAvatarStateFromMetadata(metadata, existing, layout);
          if (index == null) {
            indexById.set(merged.id, next.length);
            next.push(merged);
          } else {
            next[index] = merged;
          }
        }
        return next;
      };

      ws.onmessage = (event) => {
        let message: ServerMessage;
        try {
          message = JSON.parse(event.data) as ServerMessage;
        } catch {
          return;
        }

        switch (message.type) {
          case "init":
            setSelfId(message.selfId);
            setWaitlistPosition(
              message.waiting ? message.waitlistPosition : null
            );
            break;
          case "state":
            if (!shouldApplyStateMessage(message.seq, message.timestamp)) {
              break;
            }
            {
              recordMotionPacketArrival();
              const sampleTimeMs = mapServerTimestampToLocalMs(
                message.timestamp
              );
              const updateSeq = Number.isFinite(message.seq)
                ? message.seq
                : lastStateSeqRef.current + 1;
              applyMotionUpdates(
                message.state.players,
                sampleTimeMs,
                updateSeq
              );
              applyMotionUpdates(message.state.bots, sampleTimeMs, updateSeq);
              pruneMotionFeedToSnapshot(message.state);
              setStateSampleTimeMs(sampleTimeMs);
            }
            setRoomState(message.state);
            break;
          case "state_delta":
            if (!shouldApplyStateMessage(message.seq, message.timestamp)) {
              break;
            }
            const removedPlayers = message.state.removedPlayerIds ?? [];
            const removedBots = message.state.removedBotIds ?? [];
            if (message.state.fullSync) {
              pruneMotionFeedToSnapshot(message.state);
            } else {
              removeMotionFeedEntries([...removedPlayers, ...removedBots]);
            }
            setRoomState((prev) => {
              const previousRoom =
                prev && prev.roomId === message.state.roomId ? prev : null;
              const previousPlayers = previousRoom?.players ?? [];
              const previousBots = previousRoom?.bots ?? [];
              const treatAsFullSync =
                message.state.fullSync === true || previousRoom == null;
              return {
                roomId: message.state.roomId,
                roomType: message.state.roomType,
                layout: message.state.layout,
                theme: message.state.theme,
                players: mergeMetadataPatchIntoAvatars(
                  previousPlayers,
                  message.state.players,
                  removedPlayers,
                  treatAsFullSync,
                  previousRoom?.layout ?? message.state.layout
                ),
                bots: mergeMetadataPatchIntoAvatars(
                  previousBots,
                  message.state.bots,
                  removedBots,
                  treatAsFullSync,
                  previousRoom?.layout ?? message.state.layout
                ),
                assets: previousRoom?.assets ?? [],
                waitlistSize: message.state.waitlistSize,
                capacity: message.state.capacity,
              };
            });
            break;
          case "state_positions":
            if (!shouldApplyStateMessage(message.seq, message.timestamp)) {
              break;
            }
            {
              if (!message.state.correction) {
                recordMotionPacketArrival();
              }
              const sampleTimeMs = mapServerTimestampToLocalMs(
                message.timestamp
              );
              const updateSeq = Number.isFinite(message.seq)
                ? message.seq
                : lastStateSeqRef.current + 1;
              applyMotionUpdates(
                message.state.players,
                sampleTimeMs,
                updateSeq
              );
              applyMotionUpdates(message.state.bots, sampleTimeMs, updateSeq);
            }
            break;
          case "state_motion_intents":
            if (!shouldApplyStateMessage(message.seq, message.timestamp)) {
              break;
            }
            {
              const sampleTimeMs = mapServerTimestampToLocalMs(
                message.timestamp
              );
              const updateSeq = Number.isFinite(message.seq)
                ? message.seq
                : lastStateSeqRef.current + 1;
              applyMotionIntentUpdates(
                message.state.players,
                sampleTimeMs,
                updateSeq
              );
              applyMotionIntentUpdates(
                message.state.bots,
                sampleTimeMs,
                updateSeq
              );
            }
            break;
          case "chat":
            {
              const localSeq = chatSeqRef.current;
              chatSeqRef.current += 1;
              setRoomState((prev) => {
                if (!prev) {
                  return prev;
                }
                let changed = false;
                const patchAvatar = (
                  avatar: AvatarState
                ): AvatarState => {
                  if (avatar.id !== message.fromId) {
                    return avatar;
                  }
                  if (
                    avatar.lastMessage === message.text &&
                    avatar.lastMessageAt === message.timestamp
                  ) {
                    return avatar;
                  }
                  changed = true;
                  return {
                    ...avatar,
                    lastMessage: message.text,
                    lastMessageAt: message.timestamp,
                  };
                };
                const nextPlayers = prev.players.map(patchAvatar);
                const nextBots = prev.bots.map(patchAvatar);
                if (!changed) {
                  return prev;
                }
                return {
                  ...prev,
                  players: nextPlayers,
                  bots: nextBots,
                };
              });
              setChatLog((prev) => [
                ...prev.slice(-99),
                {
                  id: `${message.fromId}:${message.timestamp}:${localSeq}`,
                  fromName: message.fromName,
                  role: message.role,
                  text: message.text,
                  timestamp: message.timestamp,
                },
              ]);
            }
            break;
          case "waitlist":
            setWaitlistPosition(message.position);
            break;
          case "admitted":
            setWaitlistPosition(null);
            break;
          case "error":
            console.warn("[RoomConnection]", message.message);
            break;
          case "kicked":
            kicked = true;
            onKicked?.(message.message);
            ws.close();
            break;
          case "dm_notification":
            onDMNotificationRef.current?.(
              message.fromAddress,
              message.fromName,
              message.text
            );
            break;
          case "friend_request_notification":
            onFriendRequestNotificationRef.current?.(
              message.fromAddress,
              message.fromName
            );
            break;
          case "idle_warning":
            onIdleWarningRef.current?.(message.secondsLeft);
            break;
        }
      };
    }; // end connect()

    connect();

    return () => {
      cancelled = true;
      if (retryTimeout) clearTimeout(retryTimeout);
      socketRef.current?.close();
      socketRef.current = null;
      setRoomState(null);
      setStateSampleTimeMs(null);
      setSelfId(null);
      setChatLog([]);
      setWaitlistPosition(null);
      setConnected(false);
      chatSeqRef.current = 0;
      pendingReliableMessagesRef.current = [];
      minServerOffsetMsRef.current = null;
      lastStateTimestampRef.current = 0;
      lastStateSeqRef.current = 0;
      lastMotionPacketArrivedAtMsRef.current = null;
      interArrivalEmaMsRef.current = null;
      jitterEmaMsRef.current = 0;
      motionFeedRef.current.byId.clear();
      motionFeedRef.current.interpolationDelayMs =
        DEFAULT_INTERPOLATION_DELAY_MS;
    };
  }, [
    onKicked,
    roomId,
    session?.address,
    session?.displayName,
    session?.token,
  ]);

  return {
    roomState,
    stateSampleTimeMs,
    selfId,
    waitlistPosition,
    chatLog,
    connected,
    sendSocket,
    canvasRef,
    motionFeedRef,
  };
}
